import { useCallback } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

interface UseRedirectUrl {
  currentUrl: string;
  redirectUrl: string | null;
  forwardRedirectUrl: (path: string) => string;
  setRedirectUrl: (path: string) => string;
}

/**
 * React hook для работы с безопасным URL редиректа через query-параметр.
 *
 * Основные возможности:
 * - Получение текущего URL страницы в виде закодированного значения для передачи в query-параметре.
 * - Извлечение redirect URL из query-параметров.
 * - Добавление redirect URL к произвольному пути (с сохранением существующих query-параметров).
 * - "Проброс" текущего redirect URL дальше по цепочке (forward).
 *
 * @param {string} [queryParamName="redirect_url"] —
 *   Название query-параметра, в котором хранится URL редиректа.
 *
 * @returns {{
 *   currentUrl: string,
 *   redirectUrl: string | null,
 *   forwardRedirectUrl: (path: string) => string,
 *   setRedirectUrl: (path: string) => string
 * }} Объект с утилитами для работы с redirect URL.
 *
 * @property {string} currentUrl —
 *   Закодированный текущий URL (pathname + search + hash),
 *   готовый для передачи как query-параметр.
 *
 * @property {string|null} redirectUrl —
 *   Значение redirect URL, полученное из query-параметров текущей страницы
 *   (или `null`, если параметр отсутствует).
 *
 * @property {(path: string) => string} forwardRedirectUrl —
 *   Добавляет текущий redirect URL (если он есть) в указанный `path`.
 *   Сохраняет уже существующие query-параметры в `path`.
 *   Если redirect URL отсутствует — возвращает исходный `path` без изменений.
 *
 * @property {(path: string) => string} setRedirectUrl —
 *   Добавляет текущий URL страницы (в закодированном виде) как redirect URL
 *   в указанный `path`. Сохраняет уже существующие query-параметры в `path`.
 *
 * @example
 * // Внутри компонента
 * const { currentUrl, redirectUrl, forwardRedirectUrl, setRedirectUrl } = useRedirectUrl();
 *
 * console.log(currentUrl);
 * // => "%2Fcurrent%2Fpage%3Ffoo%3Dbar"
 *
 * console.log(redirectUrl);
 * // => "/previous/page?test=1"
 *
 * // Пробросить уже имеющийся redirect URL дальше
 * forwardRedirectUrl("/auth/login");
 * // => "/auth/login?redirect_url=%2Fprevious%2Fpage%3Ftest%3D1"
 *
 * // Установить в redirect URL текущую страницу
 * setRedirectUrl("/auth/login");
 * // => "/auth/login?redirect_url=%2Fcurrent%2Fpage%3Ffoo%3Dbar"
 */
export const useRedirectUrl = (
  queryParamName: string = "redirect_url",
): UseRedirectUrl => {
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get(queryParamName);
  const decodedRedirectUrl = redirectUrl
    ? decodeURIComponent(redirectUrl)
    : null;

  const location = useLocation();
  const fullPath = `${location.pathname}${location.search}${location.hash}`;
  const encodedCurrentUrl = encodeURIComponent(fullPath);

  const isSafeRedirectUrl = useCallback((path: string) => {
    return path.startsWith("/");
  }, []);

  const buildPathWithRedirectUrl = useCallback(
    (path: string, redirectUrl: string) => {
      if (!isSafeRedirectUrl(path)) {
        console.warn(`[useRedirectUrl] Абсолютные пути запрещены: ${path}`);
        return path;
      }

      const url = new URL(path, window.location.origin);
      url.searchParams.set(queryParamName, redirectUrl);
      return url.pathname + url.search + url.hash;
    },
    [isSafeRedirectUrl, queryParamName],
  );

  return {
    currentUrl: encodedCurrentUrl,
    redirectUrl:
      decodedRedirectUrl && isSafeRedirectUrl(decodedRedirectUrl)
        ? decodedRedirectUrl
        : null,
    forwardRedirectUrl: (path: string) => {
      if (redirectUrl) {
        return buildPathWithRedirectUrl(path, redirectUrl);
      }
      return path;
    },
    setRedirectUrl: (path: string) =>
      buildPathWithRedirectUrl(path, encodedCurrentUrl),
  };
};
